import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

interface ZoneItem {
    id: string;
    name: string;
    parent: string;
    order?: number;
}

interface ZoneTreeNode extends ZoneItem {
    children: ZoneTreeNode[];
}

interface ProjectItem {
    running: boolean;
    zoneId: string | null;
}

interface MoveZoneDialogProps {
    open: boolean;
    onClose: () => void;
    currentZoneId: string | null;
    currentZoneName: string;
    onConfirm: (newZoneId: string, newZoneName: string) => Promise<void>;
}

function buildTree(zones: ZoneItem[]): ZoneTreeNode | null {
    const rootItem: ZoneItem = { id: 'root', name: 'Root', parent: '', order: 0 };
    const all: ZoneItem[] = [rootItem, ...zones];
    const map: Record<string, ZoneTreeNode> = {};
    all.forEach((z) => {
        map[z.id] = { ...z, order: z.order ?? 0, children: [] };
    });
    all.forEach((z) => {
        if (z.parent && map[z.parent]) {
            map[z.parent].children.push(map[z.id]);
        } else if (z.id !== 'root') {
            map.root?.children.push(map[z.id]);
        }
    });
    function sortChildren(node: ZoneTreeNode): void {
        node.children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        node.children.forEach(sortChildren);
    }
    if (map.root) {
        sortChildren(map.root);
    }
    return map.root ?? null;
}

function getLeafIds(zones: ZoneItem[]): Set<string> {
    const parentIds = new Set(zones.map((z) => z.parent));
    return new Set(zones.filter((z) => !parentIds.has(z.id)).map((z) => z.id));
}

function MoveZoneDialog({ open, onClose, currentZoneId, currentZoneName, onConfirm }: MoveZoneDialogProps) {
    const [step, setStep] = React.useState<'pick' | 'confirm'>('pick');
    const [zones, setZones] = React.useState<ZoneItem[]>([]);
    const [projects, setProjects] = React.useState<ProjectItem[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [selectedZone, setSelectedZone] = React.useState<ZoneItem | null>(null);
    const [hasRunningProjects, setHasRunningProjects] = React.useState(false);
    const [expanded, setExpanded] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (open) {
            setStep('pick');
            setSelectedZone(null);
            setHasRunningProjects(false);
            loadData();
        }
    }, [open]);

    async function loadData() {
        setLoading(true);
        try {
            const [zonesData, projectsData] = await Promise.all([lediumApi.get<ZoneItem[]>('zones'), lediumApi.get<ProjectItem[]>('projects')]);
            const filteredZones = (zonesData ?? []).filter((z) => z.id !== 'root');
            setZones(filteredZones);
            setProjects(projectsData ?? []);
            setExpanded(['root', ...filteredZones.map((z) => z.id)]);
        } finally {
            setLoading(false);
        }
    }

    function handleZoneSelect(_event: React.SyntheticEvent, nodeId: string | string[] | null) {
        const id = Array.isArray(nodeId) ? nodeId[0] : nodeId;
        if (!id) {
            return;
        }
        const leafIds = getLeafIds(zones);
        if (!leafIds.has(id)) {
            return;
        }
        const zone = zones.find((z) => z.id === id);
        if (!zone) {
            return;
        }
        const running = projects.some((p) => p.running && (p.zoneId === currentZoneId || p.zoneId === id));
        setSelectedZone(zone);
        setHasRunningProjects(running);
        setStep('confirm');
    }

    async function handleConfirm() {
        if (!selectedZone) {
            return;
        }
        setSaving(true);
        try {
            await onConfirm(selectedZone.id, selectedZone.name);
        } finally {
            setSaving(false);
        }
    }

    const tree = React.useMemo(() => buildTree(zones), [zones]);
    const leafIds = React.useMemo(() => getLeafIds(zones), [zones]);

    function renderTree(node: ZoneTreeNode): React.ReactElement {
        const isRoot = node.id === 'root';
        const isLeaf = leafIds.has(node.id);
        const isCurrent = node.id === currentZoneId;

        return (
            <TreeItem
                key={node.id}
                itemId={node.id}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                        <Typography sx={{ color: isRoot || !isLeaf || isCurrent ? 'text.secondary' : 'text.primary' }}>
                            {node.name}
                            {isCurrent && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                                    (current)
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                }
            >
                {node.children.map((child) => renderTree(child))}
            </TreeItem>
        );
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            {step === 'pick' && (
                <>
                    <DialogTitle>{i18n('zone_selector_select')}</DialogTitle>
                    <DialogContent>
                        {loading && (
                            <Box sx={{ py: 1 }}>
                                <Skeleton height={40} />
                                <Skeleton height={40} />
                                <Skeleton height={40} />
                            </Box>
                        )}
                        {!loading && zones.length === 0 && <Typography color="text.secondary">No zones available.</Typography>}
                        {!loading && zones.length > 0 && tree && (
                            <SimpleTreeView
                                slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
                                expandedItems={expanded}
                                onExpandedItemsChange={(_e, itemIds) => setExpanded(itemIds)}
                                onSelectedItemsChange={handleZoneSelect}
                            >
                                {renderTree(tree)}
                            </SimpleTreeView>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>{i18n('cancel')}</Button>
                    </DialogActions>
                </>
            )}
            {step === 'confirm' && selectedZone && (
                <>
                    <DialogTitle>{i18n('zone_selector_select')}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {i18n('light_channel_move_zone_confirm', <strong>{selectedZone.name}</strong>, <strong>{currentZoneName}</strong>)}
                        </Typography>
                        {hasRunningProjects && (
                            <Typography sx={{ mt: 1 }} color="warning.main">
                                {i18n('light_channel_move_zone_confirm_running')}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setStep('pick')} disabled={saving}>
                            {i18n('cancel')}
                        </Button>
                        <Button variant="contained" color="error" onClick={handleConfirm} disabled={saving}>
                            {i18n('save')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

export default MoveZoneDialog;
